import { createAsyncThunk } from '@reduxjs/toolkit';
import type { Neo4jError } from 'neo4j-driver-core';

import { log } from '../../services/logging';
import type { Node, Relationship } from '../../types/graph';
import { getVisibleLabels } from '../perspectives/perspectives';
import { getPerspective } from '../perspectives/perspectives.selector';
import { getCategoriesWithCurrentStyle } from '../styles/styles';
import type { RootState } from '../types';
import type { ZoomOptions } from '../visualization/types';
import { zoomTo } from '../visualization/visualization';
import { NAME } from './graph';
import { addToInventory, addToVisible } from './graph.actions';
import { getNodes, selectNodes } from './nodes';

type ShowNodesAndRelationshipsPayload = {
  nodes: Node[];
  relationships: Relationship[];
  autoSelectNewNodes: boolean;
  zoomOptions?: ZoomOptions;
  isUpdateQuery?: boolean;
  recordLimitHit?: boolean;
  error?: Neo4jError;
};

type ShowNodesAndRelationshipsResult = {
  nodes: Node[];
  relationships: Relationship[];
};

export const showNodesAndRelationships = createAsyncThunk<
  ShowNodesAndRelationshipsResult,
  ShowNodesAndRelationshipsPayload,
  { state: RootState }
>(`${NAME}/showNodesAndRelationships`, async (payload: ShowNodesAndRelationshipsPayload, { dispatch, getState }) => {
  const { nodes, relationships, autoSelectNewNodes, zoomOptions } = payload;
  if (nodes.length === 0 && relationships.length === 0) {
    return { nodes, relationships };
  }

  const state = getState();

  const categories = getCategoriesWithCurrentStyle(state);
  const hideUncategorisedData = getPerspective(state)?.hideUncategorisedData ?? false;
  const categorisedLabels = getVisibleLabels(state);
  const filteredNodes = nodes.filter(
    ({ labels }) => labels.some((label) => categorisedLabels.includes(label)) || !hideUncategorisedData,
  );

  const currentNodesInventory = getNodes(state);
  const nodeIds = filteredNodes.map((node: Node) => node.id);

  const allNodeIds = new Set(nodeIds.concat(Object.keys(currentNodesInventory)));
  const filteredRelationships = removeRelationshipWithoutNodes(relationships, allNodeIds);

  const relationshipIds = filteredRelationships.map((rel: { id: string }) => rel.id);
  dispatch(addToInventory({ nodes: filteredNodes, relationships: filteredRelationships }));
  dispatch(addToVisible({ nodeIds, relationshipIds, categories }));
  dispatch(zoomTo({ nodeIds, options: zoomOptions }));

  if (autoSelectNewNodes) {
    dispatch(selectNodes(nodeIds));
  }

  return {
    nodes: filteredNodes,
    relationships: filteredRelationships,
  };
});

const removeRelationshipWithoutNodes = (relationships: Relationship[], nodeIds: Set<string>): Relationship[] => {
  return relationships.filter((rel) => {
    const isValid = nodeIds.has(rel.startId) && nodeIds.has(rel.endId);
    if (!isValid) {
      log.warn(`Relationship ${rel.id} is not loaded, the related nodes are not present ${rel.startId} , ${rel.endId}`);
    }
    return isValid;
  });
};
